import React, {
    useEffect,
    useRef,
    useContext,
    Fragment,
    useState,
    useMemo,
} from "react";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import FastTextInput from "components/FormElements/FastTextInput";
import Label from "components/FormElements/Label";
import { useAuth } from "hooks/Auth";
import WorkflowField from "Screens/Article/Fields/WorkflowField";
import AuthorField from "Screens/Article/Fields/AuthorField";
import TagField from "Screens/Article/Fields/TagField";
import UuidField from "Screens/Article/Fields/UuidField";
import ArticleMeta from "Screens/Article/Fields/ArticleMeta";
import ArticleToolbar from "Screens/Article/ArticleToolbar";
import TabContainer, { TabPanel } from "components/Surfaces/TabContainer";
import VersionStatusField from "Screens/Article/Fields/VersionStatusField";
import StaticDateField from "Screens/Article/Fields/StaticDateField";
import { ContentContext } from "context/Content";
import PublishButton from "Screens/Article/Fields/PublishButton";
import RichEditor from "components/FormElements/RichEditor";
import TextArea from "components/FormElements/TextArea";
import ExportButton from "Screens/Article/Fields/ExportButton";
import HistoryButton from "Screens/Article/Fields/HistoryButton";
import EditPreviewField from "./Fields/EditPreviewField";
import { nonce } from "Utils";
import Dam from "Screens/Article/Dam";
import { useRegisterActions, Priority } from "kbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import { LockClosedIcon } from "@heroicons/react/outline";
import { fromSqlDateTime } from "helpers/Date";
import HotTip from "components/Notifications/HotTip";
import EmbedCodeField from "./Fields/EmbedGifField";
import GenerateAltTextField from "./Fields/GenerateAltTextField";

function EditForm(props) {
    const auth = useAuth();
    const formRef = useRef();
    const [imagePreview, setImagePreview] = useState(null);
    const { state } = useContext(ContentContext);

    const kbarActions = useMemo(() => {
        if (
            state.content.lock &&
            state.content.lock.user.uuid === auth.user.uuid
        ) {
            return [
                {
                    id: "mediaSave",
                    name: "Save",
                    shortcut: ["Meta+Enter"],
                    keywords: "save",
                    perform: () => {
                        setTimeout(() => {
                            formRef.current.submitForm();
                        }, 250);
                    },
                    priority: Priority.HIGH,
                },
            ];
        } else {
            return [];
        }
    }, [state.content, auth.user]);

    useRegisterActions(kbarActions, [kbarActions]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (!state.isUserEditable) {
                return;
            }
            if (!props.onAutoSave) {
                return;
            }
            if (!formRef.current || !state.content.uuid) {
                return;
            }
            if (!formRef.current.dirty) {
                return;
            }
            props.onAutoSave(formRef.current.values);
        }, 2500);
        return () => clearInterval(interval);
    });

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: state.formValues,
            });
        });
    }, [state.loading, state.formValues]);

    useEffect(() => {
        if (!state.content.uuid) {
            return;
        }
        setImagePreview(
            state.content.attachment.imgix_url.concat(
                "?w=1000&ar=5:3&fit=crop&crop=faces&nonce=",
                nonce()
            )
        );
    }, [state.content]);

    return (
        <Formik
            innerRef={formRef}
            initialValues={state.formValues}
            onSubmit={props.onSubmit}
            enableReinitialize={false}
        >
            {({ setFieldValue }) => (
                <Form>
                    <PromptIfDirty />
                    <div className="relative">
                        <div className="sticky top-0 z-10">
                            <ArticleToolbar />
                        </div>
                        <div className="grid grid-cols-12 gap-4 px-2 sm:px-0">
                            <div className="order-last col-span-12 space-y-4 md:col-span-3">
                                <UuidField />
                                <EmbedCodeField />
                                <EditPreviewField
                                    onPreviewSave={() => {
                                        setImagePreview(
                                            state.content.attachment.imgix_url.concat(
                                                "?w=1000&ar=5:3&fit=crop&crop=faces&nonce=",
                                                nonce()
                                            )
                                        );
                                    }}
                                />
                                <ExportButton />
                                <HistoryButton />

                                <div className="space-y-4 rounded bg-white p-4 shadow-sm">
                                    <VersionStatusField />
                                    <StaticDateField
                                        label="Created At:"
                                        field="created_at"
                                        defaultValue="Unsaved"
                                    />
                                    <StaticDateField
                                        label="Modified At:"
                                        field="modified_at"
                                        defaultValue="Unsaved"
                                    />
                                    <StaticDateField
                                        label="Published At:"
                                        field="published_at"
                                        defaultValue="Draft"
                                    />
                                    <hr />
                                    <div className="mx-0 flex items-end justify-evenly space-x-4 px-0">
                                        <div className="flex-grow">
                                            <PublishButton />
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <WorkflowField />
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <TagField />
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <AuthorField />
                                </div>
                            </div>
                            <div className="order-first col-span-12 space-y-4 md:col-span-9">
                                {!state.loading &&
                                    state.content.lock?.uuid &&
                                    state.content.lock?.user?.uuid !==
                                        auth.user.uuid && (
                                        <div className="mb-4 rounded bg-white p-4 text-sm font-normal text-gray-800 shadow-sm">
                                            <LockClosedIcon className="inline h-5 w-5" />{" "}
                                            Checked out by{" "}
                                            {state.content.lock.user.name} on{" "}
                                            {fromSqlDateTime(
                                                state.content.lock.created_at
                                            ).toFormat("ff")}
                                        </div>
                                    )}
                                {state.content.uuid &&
                                state.content.attachment ? (
                                    <div>
                                        <div
                                            className="relative rounded bg-gray-300"
                                            style={{ minHeight: "200px" }}
                                        >
                                            {(state.content.attachment.type ===
                                                "quicktime" ||
                                                state.content.attachment
                                                    .type === "video") &&
                                            state.content.attachment
                                                .encoding_state ===
                                                "started" ? (
                                                <Fragment>
                                                    <img
                                                        className="relative rounded"
                                                        src="https://snworksceo.imgix.net/_11aagen/snw/bg-new-bigger.png?ar=16:9&w=600&fit=crop&dpr=2&blur=60&txt-align=middle,center&txt=Encoding..."
                                                        alt="Preview is being encoded..."
                                                    />
                                                </Fragment>
                                            ) : (
                                                <Fragment>
                                                    <img
                                                        className="relative z-0 rounded"
                                                        src={imagePreview}
                                                        alt={
                                                            state.content
                                                                .attachment
                                                                .title
                                                        }
                                                    />
                                                </Fragment>
                                            )}
                                        </div>
                                        <div className="mt-1 text-right">
                                            <a
                                                href={
                                                    state.content.attachment
                                                        .imgix_url
                                                }
                                                className="mr-2 text-xs text-green-600"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FontAwesomeIcon
                                                    icon={solid(
                                                        "external-link-alt"
                                                    )}
                                                />{" "}
                                                <span className="underline">
                                                    Preview File
                                                </span>
                                            </a>
                                            <a
                                                href={
                                                    state.content.attachment
                                                        .original_url
                                                }
                                                className="text-xs text-green-600"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FontAwesomeIcon
                                                    icon={solid(
                                                        "external-link-alt"
                                                    )}
                                                />{" "}
                                                <span className="underline">
                                                    Original File
                                                </span>
                                            </a>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="mt-0 h-52 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                    </div>
                                )}
                                {state.content.uuid && (
                                    <div>
                                        <Label>URLs</Label>
                                        {state.loading ? (
                                            <div className="mt-1 h-20 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        ) : (
                                            <div className="space-y-2 text-xs">
                                                <div>
                                                    <a
                                                        href={`${auth.publication.uri}/${state.content.uuid}`}
                                                        className="text-green-600 underline"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {auth.publication.uri}/
                                                        {state.content.uuid}
                                                    </a>
                                                </div>
                                                <div>
                                                    <a
                                                        href={
                                                            state.content
                                                                .attachment
                                                                .original_url
                                                        }
                                                        className="text-green-600 underline"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {
                                                            state.content
                                                                .attachment
                                                                .original_url
                                                        }
                                                    </a>
                                                </div>
                                                <div>
                                                    <a
                                                        href={state.content.attachment.imgix_url.concat(
                                                            "?w=2000&dpr=2"
                                                        )}
                                                        className="text-green-600 underline"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {state.content.attachment.imgix_url.concat(
                                                            "?w=2000&dpr=2"
                                                        )}
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {state.loading ? (
                                    <Fragment>
                                        <div>
                                            <Label>Title</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                        <div>
                                            <Label>Slug</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                        <div>
                                            <Label>URL Slug</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <FastTextInput
                                            label="Title"
                                            name="title"
                                        />
                                        <TextInput
                                            label="Slug"
                                            name="slug"
                                            disabled={
                                                state.loading ||
                                                state.preformingStateChange ||
                                                !state.isUserEditable
                                            }
                                            onChange={(e) => {
                                                const newVal = e.target.value
                                                    .toLowerCase()
                                                    .replace(/ {2,}/g, " ")
                                                    .replace(/ /g, "-")
                                                    .replace(/[^a-z0-9-]/g, "");

                                                setFieldValue("slug", newVal);
                                            }}
                                            helpertext="Slugs are internal references and depend on your publication's general workflow."
                                        />
                                        <TextInput
                                            label="URL Slug"
                                            name="title_url"
                                            disabled={
                                                state.loading ||
                                                state.preformingStateChange ||
                                                !state.isUserEditable
                                            }
                                            onChange={(e) => {
                                                const newVal = e.target.value
                                                    .toLowerCase()
                                                    .replace(/ {2,}/g, " ")
                                                    .replace(/ /g, "-")
                                                    .replace(/[^a-z0-9-]/g, "");

                                                setFieldValue(
                                                    "title_url",
                                                    newVal
                                                );
                                            }}
                                            helpertext="URL slugs are used to generate the URL for the article, they should be short but descriptive."
                                        />
                                        <TextInput
                                            label="Click Through Override"
                                            name="click_through"
                                            disabled={
                                                state.loading ||
                                                state.preformingStateChange ||
                                                !state.isUserEditable
                                            }
                                            placeholder="https://example.com"
                                            helpertext="Override where this media item links to when clicked."
                                        />
                                    </Fragment>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="relative z-20 mt-4">
                        {state.loading ? (
                            <div className="absolute top-0 left-0 z-50 h-full w-full bg-gray-50">
                                <div className="h-full w-full animate-pulse rounded bg-gray-300"></div>
                            </div>
                        ) : null}
                        <TabContainer>
                            <TabPanel label="Content">
                                <div className="space-y-4">
                                    <div>
                                        <Label>Caption</Label>
                                        <RichEditor name="content" />
                                    </div>
                                    <div>
                                        <Label>Alt Text</Label>
                                        <TextArea
                                            name="abstract"
                                            helpertext="Alt text assists visually impaired readers. Text should describe the image's purpose and content."
                                            rows={3}
                                        />
                                        <div className="flex items-center justify-start space-x-4">
                                            <GenerateAltTextField />
                                            <div className="flex-grow">
                                                <HotTip>
                                                    Read more:{" "}
                                                    <a
                                                        href="https://aceseditors.org/news/2020/how-to-write-great-alt-text-and-why-it-matters"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-green-600 underline"
                                                    >
                                                        How to write good alt
                                                        text
                                                    </a>
                                                    .
                                                </HotTip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel label="Meta">
                                <ArticleMeta block="content" />
                                <ArticleMeta block="content-lower" />
                            </TabPanel>
                            <TabPanel label="DAM">
                                <div className="mx-auto mb-4 max-w-screen-md space-y-4 rounded bg-white p-4">
                                    <p className="prose-sm text-gray-800">
                                        Digital Asset Management (DAM) is an
                                        important feature that helps you track,
                                        collect, and curate your content as it
                                        grows.
                                    </p>
                                </div>
                                <Dam item={state.content} />
                            </TabPanel>
                        </TabContainer>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default EditForm;
